<template>
  <EmailNotificationsTabSkeleton v-if="showSkeleton" variant="notifications" />
  <div v-else class="automations-scroll-panel" data-testid="email-notifications__active">
    <div class="fc-notification-toolbar">
      <SearchBar
        v-model="filters.search"
        placeholder="Search notifications"
        width="17.5rem"
        data-testid-prefix="email-notifications__search"
        class="fc-notification-toolbar__search"
      />
      <SearchableMultiSelectDropdown
        :value="filters.features"
        :items="typeOptions"
        item-text="label"
        item-value="value"
        label="Type"
        placeholder="All"
        data-testid-prefix="email-notifications__type"
        class="fc-notification-toolbar__type"
        @input="filters.features = $event"
      />
      <SearchableMultiSelectDropdown
        :value="filters.kinds"
        :items="triggerOptions"
        item-text="label"
        item-value="value"
        label="Trigger"
        placeholder="All"
        data-testid-prefix="email-notifications__trigger"
        class="fc-notification-toolbar__trigger"
        @input="filters.kinds = $event"
      />
      <SearchableMultiSelectDropdown
        :value="filters.status"
        :items="statusOptions"
        item-text="label"
        item-value="value"
        label="Status"
        placeholder="All"
        data-testid-prefix="email-notifications__status"
        class="fc-notification-toolbar__status"
        @input="filters.status = $event"
      />
      <v-btn
        v-if="hasActiveFilters"
        text
        class="fc-notification-toolbar__clear text-none"
        data-testid="email-notifications__clear-filter"
        @click="clearAllFilters"
      >
        Clear Filter
      </v-btn>
      <span class="fc-notification-toolbar__spacer" />
      <span class="fc-notification-toolbar__tz">{{ timezoneLabel }}</span>
      <ActivityLog module-name="financialclose_notification" hide-trigger use-mdi-history />
      <v-btn color="primary" class="text-none white--text" data-testid="email-notifications__new" @click="$emit('create')">
        New notification
      </v-btn>
    </div>
    <NotificationsTable
      ref="notificationsTable"
      fill
      :items="items"
      :headers="headers"
      :row-enricher="tableFilterRow"
      empty-text="No notifications yet"
      data-testid-prefix="email-notifications"
      @filter-active-change="hasActiveColumnFilter = $event"
    >
      <template #cell-name="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ item.name }}</div>
          <div v-if="createdByLine(item)" class="fc-notification-name__meta">{{ createdByLine(item) }}</div>
        </div>
      </template>
      <template #cell-type="{ item }">
        <NotificationFeatureCell :item="item" />
      </template>
      <template #cell-trigger="{ item }">
        <div class="fc-notification-trigger">
          <NotificationKindBadge :kind="item.kind" />
          <NotificationScheduleCell
            :schedule="item.meta && item.meta.schedule"
            :cron="item.schedulerTiming"
            :kind="item.kind"
            :next-run-at="item.nextRunAt"
          />
        </div>
      </template>
      <template #cell-locations="{ item }">
        <div class="fc-notification-inline">
          <OverflowList :items="locationNames(item)" :visible-count="2" />
        </div>
      </template>
      <template #cell-recipients="{ item }">
        <div class="fc-notification-inline">
          <NotificationRecipientChips :items="roleLabels(item)" />
        </div>
      </template>
      <template #cell-lastRun="{ item }">{{ lastRunLabel(item) }}</template>
      <template #cell-nextRun="{ item }">{{ nextRunLabel(item) }}</template>
      <template #cell-status="{ item }">
        <NotificationStatusToggle
          :value="isNotificationEnabled(item)"
          :loading="isStatusPending(item._id)"
          data-testid="email-notifications__status-toggle"
          @change="onToggle(item, $event)"
        />
      </template>
      <template #actions="{ item }">
        <NotificationActionIcon
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EDIT"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EDIT"
          data-testid="email-notifications__edit-btn"
          @click="$emit('edit', item)"
        />
        <NotificationActionIcon
          v-if="showHistory"
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EMAIL_HISTORY"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EMAIL"
          data-testid="email-notifications__email-history-btn"
          @click="$emit('view-activity', item)"
        />
        <ActivityLog :resource-id="item._id" module-name="financialclose_notification" />
        <NotificationDeleteAction
          :notification="item"
          :disabled="item.isRunning === true"
          :loading="isDeletePending(item._id)"
          data-testid="email-notifications__delete-btn"
          @confirm="onDelete(item)"
        />
      </template>
    </NotificationsTable>
    <div class="caption mt-2">{{ items.length }} notifications · {{ enabledCount }} on</div>
    <NotificationLegend />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  activeScheduleHeaders,
  EMAIL_NOTIFICATION,
  NOTIFICATION_FEATURE_LABEL,
  NOTIFICATION_TRIGGER_FILTER,
  NOTIFICATION_TYPE_FILTER,
} from '../../../../utils/constants';
import {
  createdByLine,
  locationIdsFromMeta,
  notificationNextRunDisplay,
  notificationTableFilterRow,
  resolveLocationNames,
} from '../../../../utils/notificationNameUtils';
import { isNotificationEnabled } from '../../../../utils/notificationRecordUtils';
import { browserTimezoneLabel, formatNextRunAt } from '../../../../utils/notificationScheduleUtils';
import ActivityLog from '../../../../components/ActivityLog/ActivityLog.vue';
import OverflowList from '../../../../components/OverflowList/OverflowList.vue';
import SearchBar from '../../../../components/SearchBar/SearchBar.vue';
import SearchableMultiSelectDropdown from '../../../../components/SearchableMultiSelectDropdown/SearchableMultiSelectDropdown.vue';
import {
  NotificationsTable,
  NotificationKindBadge,
  NotificationScheduleCell,
  NotificationStatusToggle,
  NotificationRecipientChips,
  NotificationDeleteAction,
  NotificationActionIcon,
  EmailNotificationsTabSkeleton,
  NotificationFeatureCell,
  NotificationLegend,
} from '../../../../components/Notifications';

export default {
  name: 'ActiveSchedulesTable',
  components: {
    EmailNotificationsTabSkeleton,
    ActivityLog,
    OverflowList,
    SearchBar,
    SearchableMultiSelectDropdown,
    NotificationsTable,
    NotificationKindBadge,
    NotificationScheduleCell,
    NotificationStatusToggle,
    NotificationRecipientChips,
    NotificationDeleteAction,
    NotificationActionIcon,
    NotificationFeatureCell,
    NotificationLegend,
  },
  props: {
    initialLoading: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    showHistory: { type: Boolean, default: true },
  },
  data() {
    return {
      filters: { search: '', features: [], kinds: [], status: [] },
      hasActiveColumnFilter: false,
      EMAIL_NOTIFICATION,
      typeOptions: NOTIFICATION_TYPE_FILTER,
      triggerOptions: NOTIFICATION_TRIGGER_FILTER,
      statusOptions: [
        { label: 'On', value: 'on' },
        { label: 'Off', value: 'off' },
      ],
    };
  },
  computed: {
    ...mapGetters('emailNotifications', [
      'activeScheduleNotifications',
      'fetchLocations',
      'enabledNotificationCount',
      'isStatusPending',
      'isDeletePending',
    ]),
    headers() {
      return activeScheduleHeaders();
    },
    timezoneLabel() {
      return browserTimezoneLabel();
    },
    enabledCount() {
      return this.enabledNotificationCount;
    },
    showSkeleton() {
      if (this.initialLoading) return true;
      return this.loading && !(this.activeScheduleNotifications || []).length;
    },
    hasActiveDropdownFilters() {
      return (this.filters.features || []).length > 0
        || (this.filters.kinds || []).length > 0
        || (this.filters.status || []).length > 0;
    },
    hasActiveFilters() {
      return this.hasActiveDropdownFilters
        || this.hasActiveColumnFilter
        || !!(this.filters.search || '').trim();
    },
    items() {
      const search = (this.filters.search || '').toLowerCase();
      const features = this.filters.features || [];
      const kinds = this.filters.kinds || [];
      const statuses = this.filters.status || [];
      return (this.activeScheduleNotifications || []).filter((item) => {
        if (search) {
          const haystack = [
            item.name,
            item.subject,
            item.feature,
            NOTIFICATION_FEATURE_LABEL[item.feature],
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(search)) return false;
        }
        if (features.length && !features.includes(item.feature)) return false;
        if (kinds.length && !kinds.includes(item.kind)) return false;
        if (statuses.length) {
          const on = isNotificationEnabled(item);
          const matchOn = statuses.includes('on') && on;
          const matchOff = statuses.includes('off') && !on;
          if (!matchOn && !matchOff) return false;
        }
        return true;
      });
    },
  },
  methods: {
    ...mapActions('emailNotifications', ['changeStatusForNotification', 'deleteNotification']),
    createdByLine,
    tableFilterRow(item) {
      return notificationTableFilterRow(item, { locations: this.fetchLocations });
    },
    clearDropdownFilters() {
      this.filters.features = [];
      this.filters.kinds = [];
      this.filters.status = [];
    },
    clearAllFilters() {
      this.filters.search = '';
      this.clearDropdownFilters();
      this.$refs.notificationsTable?.clearFilters();
    },
    locationNames(item) {
      return resolveLocationNames(locationIdsFromMeta(item.meta), this.fetchLocations);
    },
    roleLabels(item) {
      const roles = item.meta?.roles || [];
      return EMAIL_NOTIFICATION.roles
        .filter((role) => roles.includes(role.value))
        .map((role) => role.name);
    },
    lastRunLabel(item) {
      const at = item.lastRun || item.lastRunSummary?.at;
      return formatNextRunAt(at) || at || '—';
    },
    isNotificationEnabled,
    nextRunLabel(item) {
      return notificationNextRunDisplay(item);
    },
    async onToggle(item, next) {
      try {
        await this.changeStatusForNotification({
          id: item._id,
          status: next ? EMAIL_NOTIFICATION.STATUS.ENABLED : EMAIL_NOTIFICATION.STATUS.DISABLED,
        });
      } catch (error) {
        console.error('[ActiveSchedulesTable]:', error);
      }
    },
    async onDelete(item) {
      try {
        await this.deleteNotification({ id: item._id });
        this.$emit('deleted', item);
      } catch (error) {
        console.error('[ActiveSchedulesTable]:', error);
      }
    },
  },
};
</script>
